import { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { KeyboardInfo } from '@babylonjs/core/Events/keyboardEvents';
import { PointerInfo, PointerInfoBase } from '@babylonjs/core/Events/pointerEvents';
import { BoundingBoxInfo } from '@view/modules';
import { AnnotationNode } from './annotations';
import { eConnectionMode, eDragMode, eMateType } from './enums';
import { Connector } from './features/connector';
import { Kb3dManager } from './kb3d-manager';
import { Kb3dObject } from './kb3d-object';
import { MateNode } from './mates/mate-node';
import { isPickable } from './meshes/mesh-utilities';
import { SpaceNode } from './meshes/space-node';
import { KbVector } from './primitives';

export enum eHandler {
    click = 'click',
    doubleClick = 'doubleClick',
    draggable = 'draggable',
    hotspot = 'hotspot',
    boundingBox = 'boundingBox',
}

export type PointerInfoMinimal = PointerInfoBase & Pick<PointerInfo, 'pickInfo'>;

export interface IKb3dPickInfo {
    pickInfo: PickingInfo;

    /** the specific node that the pointer is over */
    targetNode?: SpaceNode;

    /** the 3d point in the world where the mesh was clicked */
    pickedPoint?: KbVector;
    /** the 3d point of the target mesh that was clicked */
    meshPoint?: KbVector;

    /** UV intersection coordinates */
    u?: number;
    v?: number;

    getNormal: () => KbVector | undefined;
}

export interface IKb3dPointerArgs extends PointerInfoBase, IKb3dPickInfo {
    /** the node that the handler was set on (could be an ancestor of the targetNode) */
    sourceNode?: SpaceNode;
    /** whether the event should continue to bubble up the ancestor nodes */
    propagate?: boolean;
    /** the type of pointer device.  Eg:  'mouse', 'touch', etc. */
    pointerType?: string;
    /** the mouse button that was pressed (if there is one).  0 = left, 1 = center, 2 = right */
    button?: number;
    /** whether the ctrl key was pressed during the click */
    ctrlKey?: boolean;
    /** whether the shift key was pressed during the click */
    shiftKey?: boolean;
    /** whether the alt key was pressed during the click */
    altKey?: boolean;
    /** the screen coordinates of the click in relation to the canvas viewer */
    canvasX?: number;
    canvasY?: number;
}

export interface IKb3dFacetSelectArgs extends IKb3dPointerArgs {
    selectedFaces: number[];
}

export interface IKeyboardArgs extends KeyboardInfo {
    key: string;
    keyCode: number;
    altKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    repeat: boolean;
}

export type Kb3dPointerHandler = (args: IKb3dPointerArgs) => void;

export type Kb3dBoundingBoxChangeHandler = {
    mode: 'once' | 'forever';
    handler: (args: BoundingBoxInfo) => void;
};

export interface IDragArgs {
    dragNode: SpaceNode;
    newPos: KbVector;
    startPos: KbVector;
    newRotationEuler: KbVector;
    newRotationAxis: KbVector;
    newRotationAngle: number;
    allow: boolean;
    u: number;
    v: number;
    //relativeSurfacePos: IVector2;

    /*following only used for connections*/
    connection?: IConnection;

    /** if withMates is true for the draggable, then this will contain the added mate in the dropped rule */
    mate?: MateNode;
}

export interface IConnection {
    dragConnector: Connector;
    otherConnector: Connector;
    dragNode?: SpaceNode;
    otherNode?: SpaceNode;
}

export interface ICanConnectArgs extends IConnection {
    allow: boolean;
}

// export enum eDragMode {
//     free,
//     plane,
//     //axis,
//     surface
// }

// export enum eConnectorMode {
//     none,
//     world,
//     screen
// }

export interface IDraggable {
    /** The id, name, or wildcard of the node to define as draggable */
    id?: string | SpaceNode;
    /** The mode of the drag */
    mode?: eDragMode; // free, plane, surface
    /** In plane or linear mode, is a vector normal to the drag plane.  This normal + the picked point on the mesh will determine the intersection plane */
    planeNormal?: KbVector;
    // /** In plane mode, it is the origin of the plane.  If not provided, the origin of the drag node is used (usually the desired effect) */
    // planeOrigin?: KbVector;
    /** if disabled, the node origin will snap to the mouse position when you start dragging */
    disableOffset?: boolean;

    //axis?: KbVector;

    /** In surface mode, holds the id (if a singular surface) or ids (if multiple surfaces) of the surfaces */
    surfaceNodes?: string | string[];

    /** whether connectors are enabled during this drag operation and if so how the gauge proximity */
    connectionMode?: eConnectionMode;
    /** how to align the connectors when a connection is made */
    connectionType?: eMateType;
    /** whether to show the connector spheres or not */
    showConnectors?: boolean;
    /** the proximity distance between connectors that counts as a possible connection */
    connectRadius?: number;
    /** when a connector drag, controls the opacity of the dragged items */
    dragOpacity?: number;
    /**
     * Will add mates once a connection is made.  Also uses mates to understand what connectors
     * are open for connection (meaning not part of mate).  Will dynamically swap mate connectors
     * based on the drag node.  Only valid if connectionMode is not 'none'
     * */
    withMates?: boolean;

    /** Called when the drag is started */
    dragStart?: (args: IDragArgs) => void;
    // /** Called at every frame to see if we want to allow the move */
    // allow?: (args: IDragArgs) => boolean;
    /** Called at every frame while dragging.  Can be used to custom update the position by modifying newPos */
    dragging?: (args: IDragArgs) => void;
    /** Called when the node is dropped */
    dropped?: (args: IDragArgs) => void;

    canConnect?: (args: ICanConnectArgs) => void;
}

/** A 2 layer map that stores values by a key 1st, then a 2nd level of a string tag, then an array of values */
export class TagArrayMap<TKey, TValue> {
    private _db = new Map<TKey, ArrayMap<string, TValue>>();

    public get(key: TKey, tag: string) {
        const v = this._db.get(key)?.get(tag);
        return v || [];
    }

    public deleteByKeyValue(key: TKey, value: TValue) {
        const e = this._db.get(key);
        if (e) {
            for (const values of e.values()) {
                values.removeWhere(v => v == value);
            }
            this.deleteEntryIfEmpty(key);
        }
    }

    public getByKey(key: TKey): TValue[] {
        if (this._db.has(key)) {
            return (this._db.get(key) as ArrayMap<string, TValue>).all();
        }
        return [];
    }

    public set(key: TKey, tag: string, value: TValue) {
        if (!this._db.has(key)) this._db.set(key, new ArrayMap<string, TValue>());
        this._db.get(key)?.add(tag, value);
    }

    public deleteByTag(...tags: string[]) {
        const result: Array<{ node: TKey; handlers: Record<string, TValue[]> }> = [];
        for (const [key, value] of this._db.entries()) {
            const nodeResult: Record<string, TValue[]> = {};
            result.push({ node: key, handlers: nodeResult });

            for (const tag of tags) {
                const handler = value.get(tag);
                if (handler) {
                    nodeResult[tag] = handler;
                }

                value.delete(tag);
            }
            this.deleteEntryIfEmpty(key);
        }
        return result;
    }

    public delete(key: TKey, tag: string) {
        const e = this._db.get(key);
        if (e) {
            const result = e.get(tag);
            e.delete(tag);
            this.deleteEntryIfEmpty(key);
            return result;
        }
        return [];
    }

    public deleteByKey(key: TKey) {
        const handlers = this._db.get(key);
        const result: Record<string, TValue[]> = {};
        if (handlers) {
            handlers.forEach((value, key) => {
                result[key] = value;
            });
        }

        this._db.delete(key);

        return result;
    }

    public clear() {
        this._db.clear();
    }

    public has(key: TKey, tag: string) {
        return this.get(key, tag).length > 0;
    }

    protected deleteEntryIfEmpty(key: TKey) {
        const e = this._db.get(key);
        if (e && !e.size) this._db.delete(key);
    }
}

export class ArrayMap<TKey, TValue> extends Map<TKey, TValue[]> {
    public get(key: TKey) {
        return super.get(key) || [];
    }

    public add(key: TKey, ...values: TValue[]) {
        if (!this.has(key)) {
            super.set(key, [...values]);
        } else {
            this.get(key).push(...values);
        }
    }

    public removeValue(key: TKey, ...values: TValue[]) {
        const arr = super.get(key);
        if (arr) {
            arr.removeWhere(i => values.some(v => v == i));
        }
    }

    public all() {
        return [...this.values()].flat();
    }
}

export class SetMap<TKey, TValue> extends Map<TKey, Set<TValue>> {
    public get(key: TKey) {
        const set = super.get(key);
        if (!set) super.set(key, new Set<TValue>());
        return super.get(key)!;
    }

    public add(key: TKey, ...values: TValue[]) {
        const s = this.get(key);
        for (const v of values) s.add(v);
    }

    public removeValue(key: TKey, ...values: TValue[]) {
        const s = super.get(key);
        if (s) {
            for (const v of values) s.delete(v);
        }
    }

    public all() {
        return [...this.values()].flat();
    }
}

export class HandlerMap<THandler> extends TagArrayMap<SpaceNode | AnnotationNode, THandler> {
    constructor(private manager: Kb3dManager) {
        super();
    }

    public add(idNameOrNode: string | SpaceNode | AnnotationNode, tag: string, handler: THandler) {
        let nodes = new Array<Kb3dObject>();
        if (typeof idNameOrNode == 'string') {
            nodes = this.manager.getNodesByIdOrName(idNameOrNode, true);
        } else {
            nodes.push(idNameOrNode);
        }
        for (const n of nodes) {
            if (isPickable(n)) {
                this.set(n, tag, handler);
            }
        }
    }
}
